import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import './DeviceManagementPage.css';
import AddDeviceFormModal from "./AddDeviceFormModal";
import QrCodeModal from "../QrCodeModal";
import WifiSetupModal from "./WifiSetupModal";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Typography, Button, IconButton, Collapse, Box, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPencil, faQrcode, faTrash } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useOri } from '../../context/OriContext';
import LoadingIcon from '../LoadingIcon';

export interface Device {
    deviceId: string;
    deviceName: string;
    ori: string;
    priority?: number;
    baseApiUrl: string;
    collectFace?: boolean;
    collectTwoFinger?: boolean;
}

export interface ProvisionData {
    id: string;
    qrDataUrl: string;
    url: string;
}

const fetchDevicesApi = async (ori: string): Promise<Device[]> => {
    const response = await apiFetch(`/api/risc/device?ori=${ori}`);
    if (!response.ok) throw new Error('Failed to fetch devices');
    return response.json();
};

const provisionDeviceApi = async (device: { deviceName: string, ori?: string, deviceId?: string, priority?: number }, ori: string): Promise<any> => {
    const response = await apiFetch(`/api/risc/device/provision?ori=${ori}`, {
        method: 'POST',
        body: JSON.stringify(device)
    });
    if (!response.ok) {
        let message = 'Failed to start provisioning.';
        try {
            const body = await response.json();
            if (body?.error) message = body.error;
        } catch {}
        throw new Error(message);
    }
    return response.json();
};

const saveDeviceApi = async (device: { deviceName: string, ori?: string, deviceId?: string, priority?: number, collectFace?: boolean, collectTwoFinger?: boolean }, isNew: boolean, ori: string): Promise<any> => {
    if (isNew) {
        return provisionDeviceApi(device, ori);
    } else {
        // Update existing device
        const response = await apiFetch(`/api/risc/device/${device.deviceId}`, {
            method: 'PUT',
            body: JSON.stringify({
                ori: ori,
                deviceName: device.deviceName,
                priority: device.priority,
                collectFace: device.collectFace,
                collectTwoFinger: device.collectTwoFinger,
            })
        });
        if (!response.ok) throw new Error('Failed to update device');
        return true;
    }
};

const deleteDeviceApi = async (deviceId: string, ori: string): Promise<any> => {
    const response = await apiFetch(`/api/risc/device/${deviceId}?ori=${ori}`, {
        method: 'DELETE'
    });
    if (!response.ok) throw new Error('Failed to delete device');
    return response.json();
}

const Row: React.FC<{ device: Device, onProvision: (device: Device) => void, onEdit: (device: Device) => void, onDelete: (deviceId: string) => void }> = ({ device, onProvision, onEdit, onDelete }) => {
    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell className="mobile-only" sx={{ width: '10px', padding: '0' }}>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">
                    {device.deviceName}
                </TableCell>
                <TableCell className="desktop-only">{device.deviceId}</TableCell>
                <TableCell className="desktop-only">{device.ori}</TableCell>
                <TableCell align="right">
                    <IconButton onClick={() => onEdit(device)} title="Edit Device">
                        <FontAwesomeIcon icon={faPencil} swapOpacity />
                    </IconButton>
                    <IconButton onClick={() => onProvision(device)} title="Re-provision Device">
                        <FontAwesomeIcon icon={faQrcode} swapOpacity />
                    </IconButton>
                    <IconButton onClick={() => onDelete(device.deviceId)} title="Delete Device" color="warning">
                        <FontAwesomeIcon icon={faTrash} swapOpacity />
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow className="mobile-only">
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography variant="subtitle1" gutterBottom component="div">
                                Details
                            </Typography>
                            <Table size="small" aria-label="device details">
                                <TableBody>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Device ID</TableCell>
                                        <TableCell>{device.deviceId}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Device ORI</TableCell>
                                        <TableCell>{device.ori}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell component="th" scope="row">Priority</TableCell>
                                        <TableCell>{device.priority}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
};


const DeviceManagementPage: React.FC = () => {
    const [devices, setDevices] = useState<Device[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isWifiModalOpen, setIsWifiModalOpen] = useState(false);
    const [licenseError, setLicenseError] = useState<string | null>(null);
    const [currentDevice, setCurrentDevice] = useState<Device | null>(null);
    const [provisioningData, setProvisioningData] = useState<ProvisionData | undefined>(undefined);
    const [provisioningDevice, setProvisioningDevice] = useState<Device | undefined>(undefined);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { selectedOri } = useOri();

    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [qrIsLoading, setQrIsLoading] = useState(false);
    const [qrError, setQrError] = useState<string | null>(null);
    const [isApkQr, setIsApkQr] = useState(false);

    const fetchDevices = useCallback(async () => {
        if (!selectedOri) return;
        setIsLoading(true);
        setError(null);
        try {
            const devicesData = await fetchDevicesApi(selectedOri);
            setDevices(devicesData);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [selectedOri]);

    useEffect(() => {
        fetchDevices();
    }, [fetchDevices, selectedOri]);

    const handleAddDevice = async () => {
        if (!selectedOri) return;
        try {
            const licenseResponse = await apiFetch(`/api/risc/license/agency?ori=${selectedOri}`);
            if (licenseResponse.ok) {
                const licenseInfo = await licenseResponse.json();
                if (!licenseInfo.licensed) {
                    setLicenseError('No valid license for this agency. A license must be applied before provisioning devices.');
                    return;
                }
                if (licenseInfo.licensedDeviceCount >= licenseInfo.maxDevices) {
                    setLicenseError(`No license slots available. All ${licenseInfo.maxDevices} licensed seat(s) are in use. To add a new device, remove an existing device or upgrade your license.`);
                    return;
                }
            }
        } catch {
            // If the license check itself fails, allow the modal to open so the
            // server-side check can still catch it.
        }
        setCurrentDevice(null);
        setIsAddModalOpen(true);
    };

    const handleEditDevice = (device: Device) => {
        setCurrentDevice(device);
        setIsAddModalOpen(true);
    };

    const handleSaveDevice = async (device: { deviceName: string, ori?: string, deviceId?: string, priority?: number }) => {
        if (!selectedOri) return false;
        const isNew = !currentDevice;
        const result = await saveDeviceApi(device, isNew, selectedOri); // throws on error
        setIsAddModalOpen(false);
        if (isNew) {
            setProvisioningData(result);
            setQrDataUrl(result.qrDataUrl);
            setIsApkQr(false);
            setQrModalOpen(true);
        } else {
            await fetchDevices();
        }
        return true;
    };

    const handleReprovision = async (device: Device) => {
        if (!selectedOri) return;
        setProvisioningDevice(device);
        // Clear any previous request so the modal can't poll a stale id and
        // falsely report "Device Provisioned" when this attempt fails.
        setProvisioningData(undefined);
        setQrDataUrl(null);
        setQrModalOpen(true);
        setQrIsLoading(true);
        setQrError(null);
        setIsApkQr(false);
        try {
            const provisionData = await provisionDeviceApi(device, selectedOri);
            setProvisioningData(provisionData);
            setQrDataUrl(provisionData.qrDataUrl);
        } catch (err) {
            setQrError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setQrIsLoading(false);
        }
    };

    const handleDeleteDevice = async (deviceId: string) => {
        if (window.confirm('Are you sure you want to delete this device?')) {
            if (!selectedOri) return;
            try {
                await deleteDeviceApi(deviceId, selectedOri);
                await fetchDevices();
            } catch (err) {
                console.log("Failed to delete device", err);
                setError('Failed to delete device.');
            }
        }
    };

    const handleShowApkQrCode = async () => {
        setIsApkQr(true);
        setProvisioningData(undefined);
        setQrDataUrl(null);
        setQrError(null);
        setQrModalOpen(true);
        setQrIsLoading(true);
        try {
            // QR encodes the environment's stable public download URL, which
            // redirects to the current APK release.
            const response = await apiFetch('/api/risc/app/download-qr');
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                throw new Error(data?.error || 'Failed to generate the download QR code.');
            }
            const data = await response.json();
            setQrDataUrl(data.qrDataUrl);
        } catch (err) {
            setQrError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setQrIsLoading(false);
        }
    };

    const handleQrModalClose = () => {
        setQrModalOpen(false);
        if (!isApkQr) {
            fetchDevices();
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };


    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <LoadingIcon isLoading={isLoading} showOverlay={true} />
            <Paper sx={{ p: 2, m: 1 }}>
                <div className="device-management-header">
                    <Typography variant="h6" gutterBottom component="div">
                        Device Management
                    </Typography>
                    <div>
                        <Button variant="outlined" onClick={() => setIsWifiModalOpen(true)} sx={{ mr: 1 }}>WiFi Setup</Button>
                        <Button variant="contained" onClick={handleShowApkQrCode} sx={{ mr: 1 }}>Download APK</Button>
                        <Button variant="contained" onClick={handleAddDevice}>Add Device</Button>
                    </div>
                </div>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!isLoading && (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell className="mobile-only" />
                                    <TableCell>Device Name</TableCell>
                                    <TableCell className="desktop-only">Device ID</TableCell>
                                    <TableCell className="desktop-only">Device ORI</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {devices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(device => (
                                    <Row key={device.deviceId} device={device} onProvision={handleReprovision} onEdit={handleEditDevice} onDelete={handleDeleteDevice} />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                 <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={devices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {isAddModalOpen && (
                <AddDeviceFormModal
                    device={currentDevice || undefined}
                    onClose={() => setIsAddModalOpen(false)}
                    onSave={handleSaveDevice}
                />
            )}
            <QrCodeModal
                open={qrModalOpen}
                onClose={handleQrModalClose}
                title={isApkQr ? "Download Mobile App" : "Provision Device"}
                description={isApkQr ? "Scan this QR code to download the mobile application." : "Scan this QR code with the new device to complete provisioning."}
                qrDataUrl={qrDataUrl}
                setQrDataUrl={setQrDataUrl}
                isLoading={qrIsLoading}
                error={qrError}
                device={provisioningDevice}
                provisioningData={isApkQr ? undefined : provisioningData}
                onProvisioned={handleQrModalClose}
            />

            {isWifiModalOpen && (
                <WifiSetupModal onClose={() => setIsWifiModalOpen(false)} />
            )}

            {/* License error — blocks opening the Add Device form */}
            <Dialog open={!!licenseError} onClose={() => setLicenseError(null)}>
                <DialogTitle>Cannot Add Device</DialogTitle>
                <DialogContent>
                    <DialogContentText>{licenseError}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLicenseError(null)} autoFocus>OK</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DeviceManagementPage;
